import { useCallback, useState } from "react";
import * as Popover from "@radix-ui/react-popover";
import type { DatePickerProps } from "./types";
import { DatePickerContext } from "./DatePickerContext";

export function DatePickerRoot({
  value,
  onChange,
  minDate,
  maxDate,
  autoConfirm,
  children,
}: DatePickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [pendingDate, setPendingDateRaw] = useState<Date | undefined>(value);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (open) {
        setPendingDateRaw(value);
      } else if (!value) {
        // No existing value — auto-confirm whatever was picked on close
        setPendingDateRaw((pending) => {
          if (pending) onChange(pending);
          return pending;
        });
      }
      setIsOpen(open);
    },
    [value, onChange],
  );

  const handleSetPendingDate = useCallback(
    (date: Date | undefined) => {
      setPendingDateRaw(date);
      if (autoConfirm) {
        onChange(date);
        setIsOpen(false);
      }
    },
    [autoConfirm, onChange],
  );

  const confirm = useCallback(() => {
    onChange(pendingDate);
    setIsOpen(false);
  }, [onChange, pendingDate]);

  const cancel = useCallback(() => {
    setPendingDateRaw(value);
    setIsOpen(false);
  }, [value]);

  const clear = useCallback(() => {
    onChange(undefined);
    setIsOpen(false);
  }, [onChange]);

  return (
    <DatePickerContext.Provider
      value={{
        selectedDate: value,
        pendingDate,
        setPendingDate: handleSetPendingDate,
        isOpen,
        setIsOpen,
        minDate,
        maxDate,
        confirm,
        cancel,
        clear,
      }}
    >
      <Popover.Root open={isOpen} onOpenChange={handleOpenChange}>
        {children}
      </Popover.Root>
    </DatePickerContext.Provider>
  );
}
